/**
 * Content catalogue for the SovEU service detail pages under /diensten/.
 * The overview page /diensten remains the hub and links to all entries here.
 *
 * Shape:
 *  { slug, to, h1, navLabel, anchorText, seoTitle, seoDescription, summary,
 *    intro: [string], sections: [{ h2, paragraphs?: [], bullets?: [] }],
 *    faqs: [{ q, a }], relatedServices: [slug], relatedArticles: [slug],
 *    ctaTitle, ctaText, ctaLabel }
 */
export const SERVICES = [
  {
    slug: "bestuursscan-ai-en-data",
    to: "/diensten/bestuursscan-ai-en-data",
    h1: "Bestuursscan AI en Data",
    navLabel: "Bestuursscan AI en Data",
    anchorText: "Bestuursscan AI en Data voor publieke organisaties",
    seoTitle: "Bestuursscan AI en Data voor publieke organisaties | SovEU",
    seoDescription:
      "SovEU brengt AI gebruik, datastromen, algoritmen, cloudafhankelijkheden en datarisico's bestuurlijk in beeld voor publieke organisaties.",
    summary:
      "Een bestuurlijke AI scan die AI gebruik, algoritmen, datastromen, cloudafhankelijkheden en datarisico's in één helder risicobeeld samenbrengt.",
    intro: [
      "De Bestuursscan AI en Data is de bestuurlijke AI scan van SovEU. In korte tijd brengen wij in beeld welke AI toepassingen, algoritmen, cloudservices en datastromen binnen uw organisatie in gebruik zijn, welke risico's daaraan verbonden zijn en welke onderwerpen bestuurlijke aandacht vragen.",
      "De scan is geen technisch auditrapport en geen juridisch oordeel. Het resultaat is een risicobeeld dat bruikbaar is in besluitvorming: begrijpelijk voor directie, college, raad van toezicht en toezichthouders, en concreet genoeg om intern prioriteiten te stellen rond AI governance en datagovernance in de publieke sector.",
    ],
    sections: [
      {
        h2: "Voor wie",
        paragraphs: [
          "De Bestuursscan AI en Data is ontwikkeld voor publieke en semipublieke organisaties waar AI gebruik en datagebruik direct raken aan burgers, huurders, studenten, cliënten en medewerkers.",
        ],
        bullets: [
          "Gemeenten en gemeentelijke samenwerkingsverbanden",
          "Onderwijsinstellingen, hogescholen en universiteiten",
          "Woningcorporaties",
          "Zorgorganisaties en GGD'en",
          "Uitvoeringsorganisaties, veiligheidsregio's en omgevingsdiensten",
          "Overige semipublieke organisaties met publieke verantwoordelijkheid",
        ],
      },
      {
        h2: "Wanneer relevant",
        bullets: [
          "Er is geen actueel, centraal overzicht van AI toepassingen en algoritmen in de organisatie.",
          "Medewerkers gebruiken generatieve AI zoals ChatGPT of Microsoft Copilot zonder duidelijk kader.",
          "Directie, college of raad van toezicht stelt vragen over AI, algoritmen en datarisico's.",
          "Er zijn signalen over onbeheerde dataopslag, ruime toegangsrechten of leveranciersafhankelijkheid.",
          "Toezichthouders, accountant of rekenkamer vragen om aantoonbare beheersing.",
          "Er staat een besluit aan over een nieuw platform, een nieuwe cloudomgeving of een AI toepassing.",
        ],
      },
      {
        h2: "Wat brengen we in kaart",
        paragraphs: [
          "De Bestuursscan volgt de dimensies van de SovEU Regiemethode. Wij inventariseren feitelijk gebruik en beoordelen dat op bestuurlijke relevantie.",
        ],
        bullets: [
          "AI toepassingen en algoritmen: waar worden zij ingezet, met welk doel en met welke menselijke tussenkomst",
          "Generatief AI gebruik door medewerkers, inclusief openbare AI tools",
          "Datastromen: welke gegevens worden verwerkt, waar staan zij en wie heeft toegang",
          "Cloudafhankelijkheden en de jurisdictie waaronder leveranciers vallen",
          "Documentatie: AI register, algoritmeregister, verwerkingsregister, DPIA's",
          "Rollen en eigenaarschap: wie besluit, wie beheert, wie ziet toe",
          "Datarisico's: gevoelige gegevens, bewaartermijnen, ongestructureerde opslag",
          "Bestuurlijke uitlegbaarheid: kan de organisatie verantwoorden wat er gebeurt",
        ],
      },
      {
        h2: "Wat krijgt u",
        bullets: [
          "Een bestuurlijk risicobeeld op één samenhangende set dimensies",
          "Een overzicht van AI toepassingen, algoritmen en kritieke datasystemen",
          "De belangrijkste bestuurlijke risico's, geordend op urgentie en impact",
          "Een duiding van relevante kaders zoals AVG, AI Act en informatiebeveiliging",
          "Een actielijst met eerste maatregelen die de organisatie zelf kan oppakken",
          "Een aanbeveling voor een logische vervolgstap",
          "Een mondelinge toelichting aan directie, bestuur of managementteam",
        ],
      },
      {
        h2: "Werkwijze",
        paragraphs: [
          "De scan is bewust compact opgezet en belast de organisatie zo licht mogelijk. Wij werken met bestaande documentatie en een beperkt aantal gesprekken met sleutelpersonen.",
        ],
        bullets: [
          "Intake met bestuurlijk opdrachtgever over vraag, context en scope",
          "Documentanalyse van beleid, registers, contracten en eerdere onderzoeken",
          "Gesprekken met sleutelpersonen zoals CIO, CISO, FG of DPO, informatiemanagement en proceseigenaren",
          "Analyse en beoordeling op de dimensies van de SovEU Regiemethode",
          "Terugkoppeling en bestuurlijke toelichting",
        ],
      },
      {
        h2: "Resultaat",
        paragraphs: [
          "Na de Bestuursscan weet uw organisatie waar zij staat. Niet als gevoel, maar als beeld dat u kunt delen met directie, bestuur en toezicht.",
          "Daarmee wordt AI governance een onderwerp van bestuurlijke regie in plaats van een technisch vraagstuk dat elders in de organisatie blijft hangen. Organisaties gebruiken de uitkomst doorgaans om te bepalen welke verdieping nodig is: een AI Act QuickScan, een Data en Datalek Risicoscan of een Regiebeeld Digitale Afhankelijkheden.",
        ],
      },
    ],
    faqs: [
      {
        q: "Wat is een bestuursscan AI en data precies?",
        a: "Een bestuursscan AI en data is een compacte analyse waarin AI gebruik, algoritmen, datastromen, cloudafhankelijkheden en datarisico's worden samengebracht in één bestuurlijk risicobeeld. De scan is gericht op besluitvorming, niet op technische detaildocumentatie.",
      },
      {
        q: "Is dit een audit?",
        a: "Nee. SovEU is geen auditor en geeft geen verklaring of keurmerk af. Wij leveren een onafhankelijke analyse en een risicobeeld waarmee bestuur en directie zelf kunnen prioriteren en verantwoorden.",
      },
      {
        q: "Krijgen wij een juridisch oordeel over AVG of AI Act?",
        a: "Nee. Wij duiden welke kaders relevant zijn en waar risico's zitten. Een juridisch oordeel over een specifiek systeem vraagt juridische toetsing; die kan waar nodig via gespecialiseerde partners worden toegevoegd.",
      },
      {
        q: "Wat is het verschil met een AI Act QuickScan?",
        a: "De Bestuursscan kijkt breed: AI, data, cloud, governance en datarisico's samen. De AI Act QuickScan zoomt in op AI toepassingen, risicoclassificatie, het AI register en AI Act verplichtingen.",
      },
      {
        q: "Wie moeten wij intern betrekken?",
        a: "Doorgaans de bestuurlijk opdrachtgever, CIO of informatiemanager, CISO of security officer, de functionaris gegevensbescherming en een of twee proceseigenaren uit het primaire proces.",
      },
    ],
    relatedServices: [
      "ai-act-quickscan",
      "data-datalek-risicoscan",
      "regiebeeld-digitale-afhankelijkheden",
    ],
    relatedArticles: [
      "algoritmeregister-verplicht-gemeenten",
      "digitale-afhankelijkheid-overheid-cloud-act",
      "datalek-gemeente-onderwijs-avg-data-risicos",
    ],
    praktijkvoorbeeld: {
      title: "Een uitvoeringsorganisatie die overzicht miste op AI en data",
      situatie: [
        "Een middelgrote uitvoeringsorganisatie had geen centraal overzicht van AI toepassingen, algoritmen en cloudafhankelijkheden. Directie kreeg vragen van de accountant, maar kon niet snel onderbouwen welke risico's speelden.",
        "In een compacte Bestuursscan bracht SovEU AI gebruik, datastromen en leveranciersafhankelijkheden samen in één risicobeeld, geordend op urgentie en met een eerste actielijst.",
      ],
      resultaat: [
        "Directie had één samenhangend bestuurlijk risicobeeld",
        "De belangrijkste risico's waren geprioriteerd op urgentie en impact",
        "Een logische vervolgstap lag klaar voor besluitvorming",
      ],
    },
    ctaTitle: "Bestuurlijk overzicht op AI en data",
    ctaText:
      "Wij bespreken graag waar uw organisatie nu staat, welke vragen bestuurlijk spelen en of de Bestuursscan AI en Data een logische eerste stap is.",
    ctaLabel: "Plan een vrijblijvend gesprek",
  },

  {
    slug: "ai-act-quickscan",
    to: "/diensten/ai-act-quickscan",
    h1: "AI Act QuickScan",
    navLabel: "AI Act QuickScan",
    anchorText: "AI Act QuickScan voor organisaties en adviesbureaus",
    seoTitle: "AI Act QuickScan voor organisaties en adviesbureaus | SovEU",
    seoDescription:
      "Snel inzicht in AI Act verplichtingen, AI gebruik, risicoclassificatie, AI register en governance acties.",
    summary:
      "Een gestructureerde scan van AI gebruik, risicoclassificatie, AI register en AI Act readiness, met een concrete actielijst.",
    intro: [
      "De AI Act QuickScan geeft organisaties in korte tijd een gestructureerd beeld van hun AI gebruik en de mate waarin zij zijn voorbereid op de verplichtingen die uit de AI Act voortvloeien. De scan brengt AI toepassingen samen, classificeert deze op risico en maakt zichtbaar welke documentatie, governance en afspraken ontbreken.",
      "SovEU geeft geen juridisch keurmerk en belooft geen automatische naleving. Wij helpen organisaties wel om AI Act readiness op te bouwen: overzicht van AI gebruik, onderbouwde risicoclassificatie, een werkbaar AI register en een actielijst die intern uitvoerbaar is.",
    ],
    sections: [
      {
        h2: "Voor wie",
        bullets: [
          "Publieke en semipublieke organisaties die AI inzetten in dienstverlening, toezicht of besluitvoorbereiding",
          "Gemeenten die AI Act verplichtingen willen koppelen aan het algoritmeregister",
          "Bedrijven en MKB organisaties die AI gebruiken in klantcontact, HR, planning of analyse",
          "Adviesbureaus die klantvragen over AI Act, AI governance en AI risico's krijgen",
          "Directies, FG of DPO, CISO's en compliance verantwoordelijken die een startpunt zoeken",
        ],
      },
      {
        h2: "Wanneer relevant",
        bullets: [
          "Er is geen centraal AI register en geen overzicht van AI toepassingen.",
          "Er is onduidelijkheid over de rol van de organisatie: gebruiker, aanbieder of beide.",
          "Medewerkers gebruiken generatieve AI zonder vastgesteld beleid.",
          "AI wordt ingezet in processen met gevolgen voor mensen, zoals selectie, beoordeling of prioritering.",
          "Directie of bestuur wil weten welke AI Act verplichtingen daadwerkelijk raken aan de organisatie.",
          "Een klant, opdrachtgever of ketenpartner vraagt naar AI governance en AI Act readiness.",
        ],
      },
      {
        h2: "Wat toetsen we",
        bullets: [
          "AI gebruik: welke systemen en toepassingen zijn in gebruik, met welk doel",
          "Rolbepaling per toepassing en de daaruit volgende aandachtspunten",
          "Risicoclassificatie per AI toepassing, inclusief onderbouwing",
          "Aanwezigheid en kwaliteit van het AI register en, waar relevant, het algoritmeregister",
          "Transparantie richting betrokkenen en menselijke tussenkomst bij AI ondersteunde besluiten",
          "AI geletterdheid: kennis, instructies en afspraken binnen teams",
          "Samenhang met AVG praktijk, DPIA's en informatiebeveiliging",
          "Inkoop en leveranciersafspraken rond AI functionaliteit",
        ],
      },
      {
        h2: "Wat levert het op",
        bullets: [
          "Een overzicht van AI toepassingen met risicoclassificatie per toepassing",
          "Een opzet voor een AI register dat de organisatie zelf kan bijhouden",
          "Een gap analyse: welke verplichtingen en documentatie vragen aandacht",
          "Een beeld van AI geletterdheid en waar afspraken ontbreken",
          "Een managementsamenvatting voor directie of bestuur",
          "Een geprioriteerde actielijst met concrete vervolgstappen",
        ],
      },
      {
        h2: "Vervolgstappen",
        paragraphs: [
          "De QuickScan is een startpunt, geen eindpunt. Afhankelijk van de uitkomst zetten organisaties doorgaans een van de volgende stappen.",
        ],
        bullets: [
          "AI beleid opstellen en vaststellen, inclusief afspraken over generatieve AI",
          "AI governance structureel inrichten in processen, inkoop en toezicht",
          "Verbreding naar data en cloud via een bestuursscan of risicoscan",
          "Voor gemeenten: koppeling met algoritmeregistratie en bestuurlijke verantwoording",
        ],
      },
    ],
    faqs: [
      {
        q: "Wat is een AI Act QuickScan?",
        a: "Een AI Act QuickScan is een gestructureerde beoordeling van AI gebruik binnen een organisatie: welke AI toepassingen zijn er, welk risiconiveau hoort daarbij, welke documentatie is aanwezig en welke governance acties zijn nodig.",
      },
      {
        q: "Geldt de AI Act voor onze organisatie?",
        a: "Dat hangt af van het soort AI systeem en de rol van de organisatie. Niet elke organisatie heeft dezelfde verplichtingen. De QuickScan maakt zichtbaar welke toepassingen aandacht vragen en waarom.",
      },
      {
        q: "Is een AI register verplicht?",
        a: "Voor bepaalde categorieën AI systemen gelden registratie en documentatieverplichtingen. Ook zonder harde verplichting is een AI register in de praktijk het meest effectieve instrument om overzicht en verantwoording te organiseren.",
      },
      {
        q: "Vallen ChatGPT en Copilot hieronder?",
        a: "Generatieve AI tools zijn relevant voor AI governance. Van belang is waarvoor medewerkers ze gebruiken, welke gegevens worden ingevoerd, welke afspraken gelden en hoe verantwoord gebruik wordt geborgd.",
      },
      {
        q: "Is AI Act readiness hetzelfde als compliance?",
        a: "Nee. Readiness betekent dat AI gebruik, risico's en documentatie zijn voorbereid op relevante verplichtingen. Volledige compliance hangt af van het concrete systeem, de rol van de organisatie en juridische beoordeling.",
      },
    ],
    relatedServices: [
      "bestuursscan-ai-en-data",
      "ai-beleid-opstellen",
      "algoritmeregister-gemeenten",
    ],
    relatedArticles: [
      "ai-act-readiness-2026-mkb-adviesbureaus",
      "algoritmeregister-verplicht-gemeenten",
      "datalek-gemeente-onderwijs-avg-data-risicos",
    ],
    praktijkvoorbeeld: {
      title: "Een adviesbureau dat klantvragen over de AI Act kreeg",
      situatie: [
        "Een adviesbureau zette generatieve AI in bij analyse en klantcontact en kreeg steeds vaker vragen van opdrachtgevers over AI Act readiness. Een centraal AI register en risicoclassificatie ontbraken.",
        "SovEU bracht de AI toepassingen in kaart, classificeerde ze op risico en leverde een opzet voor een AI register met een geprioriteerde actielijst.",
      ],
      resultaat: [
        "Overzicht van AI toepassingen met risicoclassificatie per toepassing",
        "Een AI register dat het bureau zelf kon bijhouden",
        "Onderbouwde antwoorden richting opdrachtgevers",
      ],
    },
    ctaTitle: "Weten waar u staat op AI Act readiness",
    ctaText:
      "In een kort gesprek verkennen wij welk AI gebruik binnen uw organisatie speelt en of een AI Act QuickScan de juiste stap is.",
    ctaLabel: "Neem contact op",
  },

  {
    slug: "algoritmeregister-gemeenten",
    to: "/diensten/algoritmeregister-gemeenten",
    h1: "Algoritmeregister voor gemeenten",
    navLabel: "Algoritmeregister voor gemeenten",
    anchorText: "Algoritmeregister voor gemeenten: registratie en governance",
    seoTitle:
      "Algoritmeregister voor gemeenten: hulp bij registratie en governance | SovEU",
    seoDescription:
      "SovEU helpt gemeenten met algoritmeregistratie, AI Act voorbereiding, AVG, DPIA's en bestuurlijke verantwoording.",
    summary:
      "Ondersteuning bij algoritmeregistratie, AI Act voorbereiding, AVG, DPIA's en bestuurlijke verantwoording over algoritmische besluitvorming.",
    intro: [
      "Steeds meer gemeenten krijgen vragen over algoritmes: van de raad, van inwoners, van journalisten, van de rekenkamer en van toezichthouders. De kern van die vragen is bijna altijd dezelfde: welke algoritmes gebruikt de gemeente, wat doen zij, welke gevolgen kunnen zij hebben voor inwoners en wie is daarvoor verantwoordelijk.",
      "SovEU helpt gemeenten om die vraag beantwoordbaar te maken. Wij ondersteunen bij algoritmeregistratie, bij de koppeling met AVG en AI Act, en bij de bestuurlijke verantwoording eromheen. Registratie is daarbij geen doel op zich, maar het zichtbare resultaat van governance die op orde is.",
    ],
    sections: [
      {
        h2: "Voor wie",
        bullets: [
          "Gemeenten die nog geen of een onvolledig algoritmeregister hebben",
          "Gemeenten die registratie hebben gestart maar deze niet actueel houden",
          "Concernjuristen, FG of DPO, informatiemanagers en CISO's",
          "Gemeentesecretarissen en directies die bestuurlijk moeten kunnen verantwoorden",
          "Gemeentelijke samenwerkingsverbanden en uitvoeringsorganisaties",
        ],
      },
      {
        h2: "Wanneer relevant",
        bullets: [
          "De raad of een raadsfractie stelt vragen over algoritmische besluitvorming in de gemeente.",
          "Er is geen actueel overzicht van algoritmes in het sociaal domein, toezicht, handhaving, belastingen of dienstverlening.",
          "Er bestaat onduidelijkheid over welke toepassingen wel en niet in het register thuishoren.",
          "DPIA's ontbreken of zijn niet geactualiseerd bij gewijzigd gebruik.",
          "Generatieve AI wordt gebruikt in gemeentelijke processen zonder registratie of kader.",
          "De gemeente wil zich voorbereiden op registratieverplichtingen die eraan komen.",
        ],
      },
      {
        h2: "Wat moet in beeld",
        bullets: [
          "Welke algoritmes en AI toepassingen in gebruik zijn, per domein en per proces",
          "Het doel van iedere toepassing en de rol in besluitvorming of prioritering",
          "De mate van menselijke tussenkomst en hoe die feitelijk werkt",
          "Welke persoonsgegevens worden verwerkt en op welke grondslag",
          "Impact op inwoners en de vraag of een DPIA aanwezig en actueel is",
          "Leverancier, jurisdictie en contractuele afspraken",
          "Eigenaarschap: wie is functioneel en bestuurlijk verantwoordelijk",
          "Hoe de gemeente uitlegt wat een algoritme doet, in begrijpelijke taal",
        ],
      },
      {
        h2: "Koppeling met AVG en AI Act",
        paragraphs: [
          "Algoritmeregistratie staat niet los van de AVG praktijk. Waar persoonsgegevens worden verwerkt, gelden verplichtingen rond grondslag, transparantie, dataminimalisatie, bewaartermijnen en, bij hoge impact, een DPIA. Registratie zonder onderliggende AVG documentatie levert een register op dat bestuurlijk niet houdbaar is.",
          "Daarnaast voegt de AI Act een Europese laag toe voor bepaalde AI systemen: risicoclassificatie, documentatie, menselijk toezicht en transparantie. Voor gemeenten is het praktisch om het algoritmeregister, het AI register en de AVG documentatie als één samenhangend dossier in te richten in plaats van als drie losse trajecten.",
        ],
      },
      {
        h2: "Wat levert SovEU",
        bullets: [
          "Een inventarisatie van algoritmes en AI toepassingen per domein",
          "Beoordeling welke toepassingen registratie en verdieping vragen",
          "Registratieteksten die begrijpelijk zijn voor inwoners en raad",
          "Toetsing van de samenhang met AVG documentatie en DPIA's",
          "Een werkproces waarmee het register actueel blijft bij nieuwe toepassingen",
          "Rollen en eigenaarschap: wie registreert, wie beoordeelt, wie stelt vast",
          "Een bestuurlijke memo voor college, directie of raad",
        ],
      },
    ],
    faqs: [
      {
        q: "Is een algoritmeregister verplicht voor gemeenten?",
        a: "Publicatie in het Nationaal Algoritmeregister is nog niet voor alle algoritmes algemeen verplicht, maar de richting is duidelijk: registratie van impactvolle algoritmes wordt wettelijk verplicht. Daarnaast gelden nu al AVG verplichtingen rond documentatie en transparantie.",
      },
      {
        q: "Welke algoritmes horen in het register?",
        a: "In de praktijk vooral toepassingen met gevolgen voor inwoners: sociaal domein, toezicht en handhaving, belastingen en heffingen, vergunningverlening, bezwaarprocedures en digitale dienstverlening. Interne hulpmiddelen zonder impact op besluiten vragen minder zwaar registratie, maar wel intern overzicht.",
      },
      {
        q: "Moet generatieve AI ook worden geregistreerd?",
        a: "Als generatieve AI wordt gebruikt in gemeentelijke processen, verdient dat in ieder geval intern overzicht en beleid. Bij inzet rond inwonerscontact, Woo verzoeken, bezwaar of beleidsvoorbereiding is registratie of expliciete verantwoording verstandig.",
      },
      {
        q: "Wat is het verschil tussen het algoritmeregister en een AI register?",
        a: "Het algoritmeregister is de publieke verantwoording naar buiten. Een AI register is het interne overzicht met risicoclassificatie, eigenaarschap en documentatie. Het interne register is de basis waaruit publieke registratie wordt gevoed.",
      },
      {
        q: "Doet SovEU de registratie voor ons?",
        a: "Wij ondersteunen bij inventarisatie, beoordeling, registratieteksten en het werkproces. De gemeente blijft zelf verwerkingsverantwoordelijke en stelt registratie zelf vast.",
      },
    ],
    relatedServices: [
      "ai-act-quickscan",
      "bestuursscan-ai-en-data",
      "ai-beleid-opstellen",
    ],
    relatedArticles: [
      "algoritmeregister-verplicht-gemeenten",
      "ai-act-readiness-2026-mkb-adviesbureaus",
      "datalek-gemeente-onderwijs-avg-data-risicos",
    ],
    praktijkvoorbeeld: {
      title: "Een gemeente die haar algoritmeregister actueel wilde maken",
      situatie: [
        "Een gemeente was ooit gestart met registratie in het Algoritmeregister, maar het overzicht was verouderd en registratieteksten waren voor inwoners lastig te begrijpen. Bij nieuwe toepassingen werd registratie vergeten.",
        "SovEU inventariseerde algoritmes per domein, toetste de samenhang met AVG en DPIA's en leverde begrijpelijke registratieteksten met een werkproces om het register actueel te houden.",
      ],
      resultaat: [
        "Registratieteksten die begrijpelijk waren voor inwoners en raad",
        "Een werkproces waarmee het register actueel bleef",
        "Helder eigenaarschap: wie registreert, beoordeelt en stelt vast",
      ],
    },
    ctaTitle: "Algoritmeregistratie bestuurlijk op orde",
    ctaText:
      "Wij denken graag mee over de vraag waar uw gemeente staat met algoritmeregistratie, AVG documentatie en AI Act voorbereiding.",
    ctaLabel: "Plan een vrijblijvend gesprek",
  },

  {
    slug: "data-datalek-risicoscan",
    to: "/diensten/data-datalek-risicoscan",
    h1: "Data en Datalek Risicoscan",
    navLabel: "Data en Datalek Risicoscan",
    anchorText: "Data en Datalek Risicoscan voor gemeenten en onderwijs",
    seoTitle: "Data en Datalek Risicoscan voor gemeenten en onderwijs | SovEU",
    seoDescription:
      "Breng AVG risico's, datalekrisico, ongestructureerde opslag, AI gebruik en leveranciersrisico's bestuurlijk in beeld.",
    summary:
      "Bestuurlijk inzicht in datalekrisico, AVG risico's, ongestructureerde opslag, toegangsrechten en leveranciersrisico's.",
    intro: [
      "Datalekken bij gemeenten en onderwijsinstellingen ontstaan zelden door één spectaculaire aanval. Veel vaker zijn het opgetelde kleine dingen: een oude map met dossiers die nooit is opgeruimd, een export die per mail rondgaat, ruime toegangsrechten in een samenwerkingsomgeving, een leverancier die meer data verwerkt dan nodig, of een AI tool waarin gevoelige informatie wordt ingevoerd.",
      "De Data en Datalek Risicoscan brengt dat type risico bestuurlijk in beeld. Wij kijken naar waar gevoelige data staat, wie erbij kan, hoe lang die data wordt bewaard, welke leveranciers een rol spelen en hoe de organisatie zou reageren als het misgaat.",
    ],
    sections: [
      {
        h2: "Voor wie",
        bullets: [
          "Gemeenten met gevoelige dossiers in sociaal domein, schuldhulp, handhaving en zorg",
          "Onderwijsinstellingen met studentgegevens, dossiers en leeromgevingen",
          "Woningcorporaties met huurdersdossiers en betalingsgegevens",
          "Zorg en maatschappelijke organisaties met cliëntgegevens",
          "FG of DPO, CISO's, informatiemanagers en bestuurlijk verantwoordelijken",
        ],
      },
      {
        h2: "Wanneer relevant",
        bullets: [
          "Er is onduidelijkheid over waar gevoelige data zich precies bevindt.",
          "Er is een incident of bijna incident geweest en de organisatie wil structureel inzicht.",
          "Toegangsrechten zijn in de loop van jaren gegroeid en nooit opgeschoond.",
          "Er staan grote hoeveelheden ongestructureerde data in mail, netwerkschijven en samenwerkingsomgevingen.",
          "Medewerkers gebruiken AI tools waarin mogelijk persoonsgegevens worden ingevoerd.",
          "De organisatie weet niet zeker of zij een melding binnen 72 uur zou kunnen onderbouwen.",
        ],
      },
      {
        h2: "Wat onderzoeken we",
        bullets: [
          "Welke categorieën gevoelige en bijzondere persoonsgegevens worden verwerkt",
          "Waar data staat: kernsystemen, mail, netwerkschijven, cloudopslag, samenwerkingsomgevingen",
          "Ongestructureerde data: schaduwadministraties, exports, oude dossiers, back-ups",
          "Toegangsrechten en het feitelijke gebruik daarvan",
          "Bewaartermijnen en of vernietiging werkelijk plaatsvindt",
          "Leveranciersrisico's: verwerkersovereenkomsten, subverwerkers, jurisdictie",
          "AI gebruik en het risico dat gevoelige data in openbare AI tools terechtkomt",
          "Samenhang met AVG documentatie: verwerkingsregister, DPIA's, grondslagen",
        ],
      },
      {
        h2: "Datalekrespons en 72 uur melding",
        paragraphs: [
          "Bij een datalek is de eerste 72 uur bepalend, niet alleen juridisch maar ook bestuurlijk. Organisaties moeten in korte tijd kunnen vaststellen welke gegevens betrokken zijn, hoeveel mensen het raakt, wat de mogelijke gevolgen zijn en welke maatregelen zijn genomen. Dat lukt alleen als de onderliggende informatie op orde is.",
        ],
        bullets: [
          "Is er een meldproces dat bekend is bij medewerkers en leveranciers",
          "Kan de organisatie snel vaststellen welke data en welke betrokkenen geraakt zijn",
          "Zijn rollen en besluitvorming tijdens een incident duidelijk belegd",
          "Zijn afspraken met leveranciers over melding en medewerking geregeld",
          "Is er een lijn voor communicatie naar betrokkenen, raad, toezicht en media",
          "Wordt na een incident geëvalueerd en aantoonbaar bijgestuurd",
        ],
      },
      {
        h2: "Wat krijgt u",
        bullets: [
          "Een risicobeeld van datalekrisico en AVG risico's per domein",
          "Een overzicht van kwetsbare dataconcentraties en ongestructureerde opslag",
          "Bevindingen over toegangsrechten, bewaartermijnen en leveranciersketens",
          "Een beoordeling van de datalekrespons en de 72 uur praktijk",
          "Een geprioriteerde actielijst met maatregelen die direct effect hebben",
          "Een bestuurlijke samenvatting voor directie, bestuur of toezicht",
        ],
      },
    ],
    faqs: [
      {
        q: "Wat is een datalek risicoscan?",
        a: "Een datalek risicoscan brengt in beeld waar gevoelige data staat, wie erbij kan, welke leveranciers een rol spelen en hoe de organisatie zou reageren bij een incident. Het resultaat is een bestuurlijk risicobeeld met concrete maatregelen.",
      },
      {
        q: "Is dit een penetratietest of technische security audit?",
        a: "Nee. Wij testen geen systemen op technische kwetsbaarheden. De scan richt zich op datagovernance, AVG praktijk, toegangsrechten, bewaartermijnen, leveranciersketens en incidentgereedheid.",
      },
      {
        q: "Wanneer moet een datalek gemeld worden?",
        a: "Een datalek moet in beginsel binnen 72 uur na ontdekking bij de Autoriteit Persoonsgegevens worden gemeld, tenzij het onwaarschijnlijk is dat het lek een risico oplevert voor betrokkenen. Bij hoog risico moeten ook betrokkenen worden geïnformeerd.",
      },
      {
        q: "Waarom is ongestructureerde data zo'n risico?",
        a: "Omdat niemand precies weet wat er staat. Exports, oude dossiers en mailbijlagen vallen buiten reguliere bewaartermijnen en toegangsbeheer, terwijl zij vaak wel gevoelige gegevens bevatten.",
      },
      {
        q: "Kan AI gebruik tot een datalek leiden?",
        a: "Ja. Als medewerkers persoonsgegevens invoeren in openbare AI tools zonder afspraken daarover, kan dat een onrechtmatige verwerking of een datalek opleveren. Daarom is AI beleid onderdeel van datagovernance.",
      },
    ],
    relatedServices: [
      "bestuursscan-ai-en-data",
      "ai-beleid-opstellen",
      "regiebeeld-digitale-afhankelijkheden",
    ],
    relatedArticles: [
      "datalek-gemeente-onderwijs-avg-data-risicos",
      "digitale-afhankelijkheid-overheid-cloud-act",
      "algoritmeregister-verplicht-gemeenten",
    ],
    praktijkvoorbeeld: {
      title: "Een onderwijsinstelling na een bijna-incident",
      situatie: [
        "Bij een onderwijsinstelling was een export met studentgegevens bijna verkeerd verstuurd. De instelling besefte dat zij niet precies wist waar gevoelige data stond en wie erbij kon.",
        "SovEU bracht dataconcentraties, ongestructureerde opslag, toegangsrechten en de 72 uur incidentgereedheid in beeld, met een geprioriteerde actielijst.",
      ],
      resultaat: [
        "Inzicht in kwetsbare dataconcentraties en oude exports",
        "Opgeschoonde prioritering van toegangsrechten",
        "Een beoordeeld meldproces voor de eerste 72 uur",
      ],
    },
    ctaTitle: "Datalekrisico bestuurlijk in beeld",
    ctaText:
      "Wij verkennen graag met u waar de grootste dataconcentraties en risico's binnen uw organisatie zitten en welke stap logisch is.",
    ctaLabel: "Neem contact op",
  },

  {
    slug: "regiebeeld-digitale-afhankelijkheden",
    to: "/diensten/regiebeeld-digitale-afhankelijkheden",
    h1: "Regiebeeld Digitale Afhankelijkheden",
    navLabel: "Regiebeeld Digitale Afhankelijkheden",
    anchorText: "Regiebeeld Digitale Afhankelijkheden voor overheden",
    seoTitle: "Regiebeeld Digitale Afhankelijkheden voor overheden | SovEU",
    seoDescription:
      "Inzicht in afhankelijkheid van Microsoft, AWS, Google, SaaS, AI platforms, CLOUD Act en niet Europese technologie.",
    summary:
      "Inzicht in digitale afhankelijkheid van niet Europese technologie, jurisdictie, leveranciersketens en Europese alternatieven.",
    intro: [
      "Vrijwel elke Nederlandse overheidsorganisatie is voor haar primaire processen afhankelijk van technologie van buiten Europa. Microsoft 365, Azure, AWS, Google Cloud, SaaS platforms en AI diensten zijn diep verweven met werkplek, dossiervorming, communicatie en dienstverlening. Dat is niet per definitie onverantwoord, maar het is wel een bestuurlijke keuze die expliciet gemaakt en periodiek herzien moet worden.",
      "Het Regiebeeld Digitale Afhankelijkheden maakt die afhankelijkheid zichtbaar: per categorie, per leverancier, per jurisdictie en per risicoscenario. Zodat digitale soevereiniteit geen abstract begrip blijft, maar een onderwerp wordt waarover bestuur en directie feitelijk kunnen besluiten.",
    ],
    sections: [
      {
        h2: "Voor wie",
        bullets: [
          "Gemeenten en gemeentelijke samenwerkingsverbanden",
          "Uitvoeringsorganisaties, veiligheidsregio's en omgevingsdiensten",
          "Onderwijsinstellingen en woningcorporaties",
          "Zorg en semipublieke organisaties met kritieke processen in de cloud",
          "CIO's, CISO's, directies en bestuurlijk verantwoordelijken",
        ],
      },
      {
        h2: "Wanneer relevant",
        bullets: [
          "Er staat een besluit aan over verlenging, migratie of uitbreiding van een cloudcontract.",
          "De raad, directie of toezichthouder stelt vragen over digitale soevereiniteit en CLOUD Act.",
          "Kritieke processen draaien volledig bij één leverancier zonder uitwijkoptie.",
          "AI functionaliteit wordt via bestaande platforms ingeschakeld zonder aparte afweging.",
          "Er is behoefte aan een onderbouwd beeld voordat over Europese alternatieven wordt gesproken.",
          "Continuïteitsscenario's zijn nooit doordacht voor het wegvallen van een platform.",
        ],
      },
      {
        h2: "Wat brengen we in kaart",
        bullets: [
          "Per categorie: werkplek, mail, opslag, samenwerking, videoconferencing, identiteitsbeheer, dossiervorming, AI diensten",
          "Welke leveranciers per categorie kritiek zijn en welke processen daarvan afhangen",
          "Concentratierisico: waar draagt één leverancier meerdere kritieke functies",
          "Vendor lock in: contractuele, technische en datamatige uitstapdrempels",
          "Leveranciersketens en subverwerkers achter de hoofdleverancier",
          "Continuïteitsscenario's bij uitval, prijsstijging of gewijzigde voorwaarden",
          "Waar data feitelijk staat en welke partijen toegang kunnen krijgen",
        ],
      },
      {
        h2: "Jurisdictie en leveranciersketens",
        paragraphs: [
          "De kernvraag bij digitale afhankelijkheid is niet alleen waar data staat, maar onder welk recht de partij valt die de data beheert. Wetgeving zoals de CLOUD Act kan aanbieders die onder Amerikaanse jurisdictie vallen verplichten tot medewerking, ook wanneer de data fysiek in een Europees datacenter staat.",
          "Wij brengen daarom in kaart welke leveranciers onder welke jurisdictie vallen, hoe de contractuele keten is opgebouwd, welke subverwerkers een rol spelen en welke waarborgen wel en niet houvast bieden. Dat levert geen alarmerend verhaal op, maar een feitelijke basis waarop bestuurlijk kan worden gewogen.",
        ],
      },
      {
        h2: "Europese alternatieven",
        paragraphs: [
          "In een groeiend aantal categorieën bestaan serieuze Europese alternatieven, bijvoorbeeld voor opslag, samenwerking, videoconferencing, mail en delen van dossiervorming. In andere categorieën zijn alternatieven er nog niet of niet in een vorm die past bij de omvang van de organisatie.",
          "Wij beoordelen per categorie wat realistisch is: wat levert meer zeggenschap op, wat is uitvoerbaar binnen bestaande contracten en beheercapaciteit, en welke stap is proportioneel gelet op risico, kosten en continuïteit. Geen ideologische keuze, maar een afgewogen bestuurlijk advies.",
        ],
      },
      {
        h2: "Wat krijgt u",
        bullets: [
          "Een overzicht van digitale afhankelijkheden per categorie en per leverancier",
          "Een jurisdictie en ketenanalyse van kritieke leveranciers",
          "Een risicobeeld met concentratierisico, lock in en continuïteitsscenario's",
          "Een beoordeling van Europese alternatieven per categorie",
          "Handelingsopties met een realistische inschatting van uitvoerbaarheid",
          "Een bestuurlijke rapportage en toelichting voor directie of bestuur",
        ],
      },
    ],
    faqs: [
      {
        q: "Wat is digitale afhankelijkheid precies?",
        a: "Digitale afhankelijkheid is de mate waarin een organisatie voor haar primaire processen afhankelijk is van technologie, leveranciers en platforms waarover zij zelf geen zeggenschap heeft. Het gaat om techniek, contracten, data en kennis samen.",
      },
      {
        q: "Wat betekent de CLOUD Act voor overheden?",
        a: "De CLOUD Act kan aanbieders die onder Amerikaanse jurisdictie vallen verplichten om data te verstrekken, ook als die data in Europa staat. Voor overheden is daarom relevant onder welke jurisdictie een leverancier valt en welke waarborgen zijn afgesproken.",
      },
      {
        q: "Moeten wij dan stoppen met Microsoft 365 of AWS?",
        a: "Dat is niet het uitgangspunt. Het doel is bewuste regie: weten waar afhankelijkheid zit, welke risico's daarbij horen, welke waarborgen gelden en welke alternatieven per categorie realistisch zijn.",
      },
      {
        q: "Is dit hetzelfde als digitale soevereiniteit?",
        a: "Digitale soevereiniteit is het bredere begrip: zeggenschap over technologie, data en keten. Het Regiebeeld maakt dat begrip concreet voor uw organisatie, met feitelijke afhankelijkheden en handelingsopties.",
      },
      {
        q: "Levert dit een migratieplan op?",
        a: "Het Regiebeeld levert een risicobeeld en handelingsopties. Wanneer de organisatie besluit een stap te zetten, kan een vervolgtraject worden ingericht; het regiebeeld vormt daarvoor de bestuurlijke onderbouwing.",
      },
    ],
    relatedServices: [
      "bestuursscan-ai-en-data",
      "data-datalek-risicoscan",
      "ai-act-quickscan",
    ],
    relatedArticles: [
      "digitale-afhankelijkheid-overheid-cloud-act",
      "datalek-gemeente-onderwijs-avg-data-risicos",
      "algoritmeregister-verplicht-gemeenten",
    ],
    praktijkvoorbeeld: {
      title: "Een gemeente voor de verlenging van haar cloudcontract",
      situatie: [
        "Een gemeente stond voor de verlenging van een groot cloudcontract en kreeg vragen uit de raad over digitale soevereiniteit en de CLOUD Act. Een feitelijk beeld van de afhankelijkheden ontbrak.",
        "SovEU bracht de afhankelijkheden per categorie en leverancier in kaart, met een jurisdictie- en ketenanalyse en een beoordeling van Europese alternatieven.",
      ],
      resultaat: [
        "Een feitelijk overzicht van digitale afhankelijkheden per categorie",
        "Zicht op concentratierisico en uitstapdrempels vóór het contractbesluit",
        "Handelingsopties met een realistische inschatting van uitvoerbaarheid",
      ],
    },
    ctaTitle: "Regie op digitale afhankelijkheid",
    ctaText:
      "Wij bespreken graag welke afhankelijkheden binnen uw organisatie bestuurlijk het meest urgent zijn en hoe een regiebeeld daarbij helpt.",
    ctaLabel: "Plan een vrijblijvend gesprek",
  },

  {
    slug: "ai-beleid-opstellen",
    to: "/diensten/ai-beleid-opstellen",
    h1: "AI beleid opstellen",
    navLabel: "AI beleid opstellen",
    anchorText: "AI beleid opstellen voor uw organisatie",
    seoTitle: "AI beleid opstellen voor organisaties | SovEU",
    seoDescription:
      "SovEU helpt organisaties met praktisch AI beleid voor ChatGPT, Copilot, generatieve AI, AVG, AI Act en medewerkersgebruik.",
    summary:
      "Praktisch AI beleid voor generatieve AI, ChatGPT en Copilot, met rollen, kaders en aantoonbare governance.",
    intro: [
      "In vrijwel elke organisatie wordt AI al gebruikt, ook als daar nog geen beleid over is vastgesteld. Medewerkers gebruiken ChatGPT om teksten te schrijven, Copilot om documenten samen te vatten en AI functies die stilzwijgend in bestaande software zijn ingeschakeld. Zonder kader ontstaat een grijs gebied waarin niemand precies weet wat mag en wat niet.",
      "SovEU helpt organisaties met AI beleid dat werkbaar is in de praktijk: duidelijk over wat is toegestaan, begrijpelijk voor medewerkers, en aantoonbaar richting AVG praktijk, AI Act en interne beheersing. Geen document voor de bureaulade, maar een kader waar mensen daadwerkelijk naar kunnen handelen.",
    ],
    sections: [
      {
        h2: "Voor wie",
        bullets: [
          "Publieke en semipublieke organisaties zonder vastgesteld AI beleid",
          "Gemeenten, onderwijsinstellingen, woningcorporaties en zorgorganisaties",
          "Bedrijven en MKB organisaties waar AI gebruik informeel is gegroeid",
          "Organisaties die Microsoft Copilot uitrollen of gaan uitrollen",
          "Directies, HR, FG of DPO, CISO's en informatiemanagers",
        ],
      },
      {
        h2: "Wat moet in AI beleid staan",
        bullets: [
          "Doel en toepassingsbereik: welke AI toepassingen vallen onder het beleid",
          "Uitgangspunten: verantwoord gebruik, transparantie, menselijke verantwoordelijkheid",
          "Toegestaan en niet toegestaan gebruik, uitgewerkt naar herkenbare situaties",
          "Regels voor persoonsgegevens, bijzondere gegevens en vertrouwelijke informatie",
          "Menselijke tussenkomst bij AI ondersteunde beoordelingen en besluiten",
          "Controle op output: feitelijke juistheid, bronvermelding en bias",
          "Beoordeling en goedkeuring van nieuwe AI toepassingen en tools",
          "Registratie in een AI register en, waar relevant, het algoritmeregister",
          "Afspraken met leveranciers over AI functionaliteit en datagebruik",
          "Melden van incidenten en onbedoeld gebruik",
        ],
      },
      {
        h2: "Openbare AI tools",
        paragraphs: [
          "De meeste risico's ontstaan niet bij grote AI projecten, maar bij dagelijks gebruik van openbare AI tools. Een medewerker die een bezwaarschrift, een cliëntdossier of een personeelsevaluatie in een publieke chatbot plakt om er snel een samenvatting van te maken, verwerkt persoonsgegevens buiten de organisatie zonder dat iemand dat ziet.",
          "Beleid dat dit categorisch verbiedt zonder alternatief te bieden, werkt in de praktijk niet: het gebruik verdwijnt dan uit het zicht in plaats van uit de organisatie. Daarom werken wij met heldere grenzen, begrijpelijke voorbeelden en een werkbare route voor legitiem gebruik.",
        ],
      },
      {
        h2: "Veilige AI omgeving",
        paragraphs: [
          "Naast regels hoort bij AI beleid een antwoord op de vraag waar medewerkers hun werk wél kunnen doen. Dat betekent afspraken over welke AI omgeving is goedgekeurd, welke gegevens daarin mogen, hoe met logging en bewaartermijnen wordt omgegaan en hoe wordt voorkomen dat organisatiedata wordt gebruikt voor het trainen van modellen.",
        ],
        bullets: [
          "Welke AI omgeving of tool is goedgekeurd en voor welk gebruik",
          "Welke gegevenscategorieën daarin wel en niet mogen",
          "Afspraken over datagebruik, training, logging en bewaartermijnen",
          "Toegang, autorisatie en beheer van AI functionaliteit",
          "Instructie en AI geletterdheid voor medewerkers en leidinggevenden",
        ],
      },
      {
        h2: "Rollen en verantwoordelijkheden",
        bullets: [
          "Bestuurlijk eigenaarschap: wie stelt AI beleid vast en wie is eindverantwoordelijk",
          "Functioneel eigenaarschap per AI toepassing",
          "Rol van FG of DPO bij verwerkingen en DPIA's",
          "Rol van CISO of security officer bij informatiebeveiliging",
          "Rol van HR bij instructie, gedrag en arbeidsrechtelijke aspecten",
          "Rol van inkoop bij toetsing van AI functionaliteit in contracten",
          "Periodieke herijking: wie beoordeelt en actualiseert het beleid",
        ],
      },
    ],
    faqs: [
      {
        q: "Waarom is AI beleid nodig als wij nog nauwelijks AI gebruiken?",
        a: "Vrijwel altijd blijkt AI al in gebruik, via generatieve AI tools en AI functies in bestaande software. Beleid zorgt ervoor dat dat gebruik zichtbaar en verantwoord verloopt in plaats van informeel.",
      },
      {
        q: "Moeten wij ChatGPT verbieden?",
        a: "Een categorisch verbod werkt in de praktijk zelden. Effectiever is duidelijkheid over welke gegevens nooit in openbare tools mogen, in combinatie met een goedgekeurde omgeving voor legitiem gebruik.",
      },
      {
        q: "Hoort Microsoft Copilot bij AI beleid?",
        a: "Ja. Copilot krijgt toegang tot bestaande documenten en mail binnen de organisatie. Daardoor zijn autorisaties, dataclassificatie en bewaartermijnen minstens zo belangrijk als het AI model zelf.",
      },
      {
        q: "Is AI beleid hetzelfde als AI Act compliance?",
        a: "Nee. AI beleid is een belangrijk onderdeel van AI governance en draagt bij aan readiness, maar volledige naleving hangt af van het concrete AI systeem, de rol van de organisatie en juridische beoordeling.",
      },
      {
        q: "Hoe voorkomen wij een document dat niemand leest?",
        a: "Door te werken met herkenbare praktijksituaties, korte instructies per rol en een duidelijke route voor nieuwe AI toepassingen. Beleid dat aansluit op het echte werk wordt gebruikt.",
      },
    ],
    relatedServices: [
      "ai-act-quickscan",
      "data-datalek-risicoscan",
      "bestuursscan-ai-en-data",
    ],
    relatedArticles: [
      "ai-act-readiness-2026-mkb-adviesbureaus",
      "datalek-gemeente-onderwijs-avg-data-risicos",
      "algoritmeregister-verplicht-gemeenten",
    ],
    praktijkvoorbeeld: {
      title: "Een organisatie die Microsoft Copilot ging uitrollen",
      situatie: [
        "Een organisatie wilde Copilot uitrollen, terwijl medewerkers al informeel generatieve AI gebruikten zonder kader. Er was onduidelijkheid over welke gegevens wel en niet in AI tools mochten.",
        "SovEU stelde werkbaar AI beleid op met heldere grenzen, herkenbare praktijkvoorbeelden en een route voor legitiem gebruik, aangesloten op AVG en autorisaties.",
      ],
      resultaat: [
        "Duidelijkheid over toegestaan en niet toegestaan AI gebruik",
        "Afspraken over gegevens, autorisaties en een goedgekeurde AI omgeving",
        "Beleid dat aansloot op het echte werk en dus werd gebruikt",
      ],
    },
    ctaTitle: "AI beleid dat werkt in de praktijk",
    ctaText:
      "Wij bespreken graag hoe AI binnen uw organisatie feitelijk wordt gebruikt en welk beleid daar realistisch bij past.",
    ctaLabel: "Neem contact op",
  },
];

export function serviceBySlug(slug) {
  return SERVICES.find((s) => s.slug === slug);
}

export function servicesBySlugs(slugs = []) {
  return slugs.map((s) => serviceBySlug(s)).filter(Boolean);
}

/** Relevant service pages per Inzichten article slug. */
export const SERVICES_FOR_ARTICLE = {
  "algoritmeregister-verplicht-gemeenten": [
    "algoritmeregister-gemeenten",
    "ai-act-quickscan",
    "bestuursscan-ai-en-data",
  ],
  "digitale-afhankelijkheid-overheid-cloud-act": [
    "regiebeeld-digitale-afhankelijkheden",
    "bestuursscan-ai-en-data",
    "data-datalek-risicoscan",
  ],
  "datalek-gemeente-onderwijs-avg-data-risicos": [
    "data-datalek-risicoscan",
    "ai-beleid-opstellen",
    "bestuursscan-ai-en-data",
  ],
  "ai-act-readiness-2026-mkb-adviesbureaus": [
    "ai-act-quickscan",
    "ai-beleid-opstellen",
    "bestuursscan-ai-en-data",
  ],
};
